<template>
    <v-app>
        <header-card-public current_page="ChangePassword"></header-card-public>
        <v-content class="bg-container">
            <v-layout fill-height row wrap>
                
                <!-- Left Side: Notifications (Desktop Only) -->
                <v-flex v-if="!isMobile" xs12 sm6 class="notifications-section">
                    <div class="notifications-bg">
                        <!-- <public-notifications></public-notifications> -->
                        <platform-showcase></platform-showcase>
                    </div>
                </v-flex>

                <!-- Right Side: Interactive Pass Update Form Column -->
                <v-flex xs12 sm6 :class="welcomeSectionClass">
                    <v-layout column fill-height align-center justify-center class="mobile-stack-container">
                        
                        <v-spacer v-if="isMobile"></v-spacer>

                        <div class="login-container">
                            <!-- Glassmorphic Login Card (Matches dimensions across other views) -->
                            <v-card :class="['mx-auto', isMobile ? 'welcome-card-mobile' : 'welcome-card']" flat>
                                <v-form @submit.prevent="executeRecaptcha" ref="formChangePassword">
                                    <v-card-text class="px-0">
                                        <v-alert v-if="error" :value="true" color="error" icon="error" class="rounded-4 mb-3" outline>
                                            {{ errorMessage }}
                                        </v-alert>
                                        
                                        <v-layout row wrap>
                                            <v-flex xs12> 
                                                <!-- Slate Blue Heading Target Layout Anchor -->
                                                <h3 class="text-xs-center welcome-message card-dark-text">Change Password</h3>  

                                                <!-- 📝 Updated: Switched utility color to card-secondary-text and advanced system placeholder calendar tracking to 2026 -->
                                                <p class="mt-10 body-2 card-secondary-text">
                                                    Your password must be at least 12 characters and include uppercase, lowercase, number, and special character. Tip: Use a memorable phrase (e.g., SunsetsAreBetter@Boracay2026).
                                                </p>

                                                <v-alert v-if="password_the_same" :value="true" color="error" icon="error" class="rounded-4 mb-2" outline>
                                                    {{dbresult_description}}
                                                </v-alert>

                                                <v-alert v-if="notthesamepassword" :value="true" color="error" icon="error" class="rounded-4 mb-2" outline>
                                                    Input passwords are not the same.
                                                </v-alert>
                                                
                                                <!-- Glass theme friendly inputs -->
                                                <v-text-field 
                                                    class="brand-input-field mt-3"
                                                    color="#1976D2"
                                                    v-model="pass1" 
                                                    :rules="[rules.required, rules.min, rules.strong_password]" 
                                                    label="Password"
                                                    hint="At least 12 characters"
                                                    counter
                                                    :disabled="disabled"
                                                    autocomplete="new-password"
                                                    :append-icon="show1 ? 'visibility_off' : 'visibility'"
                                                    :type="show1 ? 'text' : 'password'"
                                                    @click:append="show1 = !show1"
                                                    @blur="password_the_same=false"
                                                    >
                                                </v-text-field>

                                                <v-text-field 
                                                    class="brand-input-field mt-2"
                                                    color="#1976D2"
                                                    v-model="pass2" 
                                                    :rules="[rules.required, rules.min, rules.strong_password]" 
                                                    label="Confirm Password"
                                                    hint="At least 12 characters"
                                                    counter
                                                    :disabled="disabled"
                                                    autocomplete="new-password"
                                                    :append-icon="show2 ? 'visibility_off' : 'visibility'"
                                                    :type="show2 ? 'text' : 'password'"
                                                    @click:append="show2 = !show2"
                                                    @blur="password_the_same=false"
                                                    >
                                                </v-text-field>
                                            </v-flex>
                                        </v-layout> 
                                    </v-card-text>  
                                    
                                    <!-- Light separation divider link -->
                                    <div class="glass-divider my-2"></div>
                                    
                                    <v-card-actions class="px-0 pt-3">
                                        <v-spacer></v-spacer>
                                        <!-- Primary Action: Color locked to brand blue #1976D2 -->
                                        <v-btn color="#1976D2" type="submit" :loading="loading" class="no-uppercase rounded-4 primary-device-btn px-4 mr-2">Ok</v-btn>
                                        <!-- Secondary Action: Transparent matching outline layout -->
                                        <v-btn @click="$router.go(-1)" :disabled="disabled" class="no-uppercase rounded-4 secondary-password-btn px-4">Cancel</v-btn>
                                    </v-card-actions>
                                </v-form>
                            </v-card>
                        </div>
                        
                        <v-spacer v-if="isMobile"></v-spacer>

                        <!-- 💡 Fixed: Footer structure extracted cleanly beneath column boundaries to prevent center box clamping -->
                        <div class="global-desktop-footer caption">
                            <terms-privacy></terms-privacy>
                            <other-legal-docs></other-legal-docs>
                            <copyright></copyright>
                        </div>
                    </v-layout>
                </v-flex>
            </v-layout>
        </v-content>

        <recaptcha ref="recaptcha" @verify="submitReCaptcha"></recaptcha>

        <!-- Dynamic Success Feedback Dialog Box System Overlay -->
        <v-dialog v-model="dialogOkOnly" persistent width="450">
            <ok-only :msg="dialogOkOnlyMsg" :dialogOkOnly.sync="dialogOkOnly" @ok="login"></ok-only>
        </v-dialog>
    </v-app>
</template>

<script>

import '@/assets/css/public-pages.css'
import userApi from '@/api/user.js'
import Recaptcha from '@/components/ReCaptcha.vue'
import auth from '@/auth/auth.js'
import commonfn from '@/common/functions.js'    // for refactor
import publicPages from '@/helpers/public-pages.js'

export default {
    name: 'ChangePassword',
    metaInfo: {
        title: 'Change Password',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Password change on first login or after password reset' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        Recaptcha,
        'platform-showcase': () => import('@/components/PublicNotifications/PlatformShowcase'),
        'terms-privacy': () => import('@/components/Common/TermsAndPrivacy'),
        'other-legal-docs': () => import('@/components/Common/OtherLegalDocs'),
        'copyright': () => import('@/components/Common/Copyright'),
        'header-card-public': () => import('@/components/Common/HeaderPublic'),
        'public-notifications': () => import('@/components/PublicNotifications/PublicNotifications'),
        'ok-only': ()=> import("@/components/BasicPopups/OkOnly"),
    },
    data () {
        return {
            dialog: false,
            disabled: false,       
            loading: false,
            email: '',         
            error: false,  
            errorMessage: '',
            oldPassword: '',
            pass1: '', 
            pass2: '',
            show1: false,
            show2: false,
            showOld: false,
            //todo - put this in rules-helper.js
            rules: {
                required: value => !!value || 'Required.',
                min: v => v.length >= 12 || 'Min 12 characters',
                counter: value => value.length <= 50 || 'Max 50 characters',
                strong_password: value => {
                    const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/
                    return pattern.test(value) || 'Must contain at least 1 lower case, 1 upper case, 1 numeric, 1 special character.'
                },
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(value) || 'Invalid e-mail.'
                } 
            },
            recaptchaResponse: null,
            dbresult_description: null,
            password_the_same: false,
            dialogOkOnly: false,
            dialogOkOnlyMsg: 'Your password has been successfully changed. You can now proceed to log in.',
        }
    },   
    methods: {
        submitReCaptcha(response) {
            this.recaptchaResponse = response
            this.changePassword()
        },       
        executeRecaptcha() {
            if ((this.$refs.formChangePassword.validate()) && (!this.notthesamepassword)) {
                this.loading = true
                this.disabled = true
                this.$refs.recaptcha.execute()
            }
        },
        login() {
            const logoutData = {
                userId: this.$store.state.userId,
                logoutCode: "change_password",
                logoutMessage: "Change Password",
            }
            auth.logout(logoutData)

            this.dialogOkOnly = false

            this.$router.push({
                path: '/signin-with-password',
                query: { redirect: this.$route.query.redirect }
            });
        },
        clear() {
            this.$refs.formChangePassword.reset()
            this.loading = false
            this.error = false
        },
        async changePassword() {
            this.error = false

            // Check validation first to avoid unnecessary try/catch overhead
            if (!this.$refs.formChangePassword.validate() || this.notthesamepassword) {
                return
            }

            try {
                this.loading = true
                this.disabled = true

                const response = await userApi.changePassword(
                    this.userId, 
                    this.pass1, 
                    this.recaptchaResponse
                )
                
                if (response && response.status == 200) {     
                    this.dbresult_description = commonfn.validateDBResponse(response.data);
                    
                    if (!this.dbresult_description) {
                        this.clear()
                        this.dialogOkOnly = true
                    } else {
                        // Handle cases where password might be the same as old one, etc.
                        this.password_the_same = true
                        this.loading = false
                        this.disabled = false
                    }  
                }
            } catch(error) {
                this.error = true
                // Use a consistent error parser or check for response data
                this.errorMessage = error.response && error.response.data 
                    ? error.response.data.message 
                    : error.message || "An error occurred";
                
                if (!this.errorMessage) this.errorMessage = error

                this.loading = false
                this.disabled = false
            }
        }
    },
    mounted() {
        // Check the route params for the flag
        const isInitiatedBySystem = this.$route.params.fromLoginFlow

        if (!isInitiatedBySystem && !auth.loggedIn()) {
            //prevent from manually accessing change password page
            return this.$router.push({
                path: '/home',
                query: { redirect: this.$route.query.redirect }
            });
        }
        
        // Only reset if the ref exists
        if (this.$refs.formChangePassword) {
            this.$refs.formChangePassword.reset();
        }
    },
    computed: {
        userId() {
            return this.$store.state.userId
        },
        notthesamepassword: function() {
            if (this.pass1 && this.pass2)
                return this.pass1!=this.pass2
        },
        seasonDetails() {
            return publicPages.getSeasonDetails(this.$route.query.action, this.isMobile);
        },
        welcomeSectionClass() {
            return this.seasonDetails.className;
        },
        welcomeButtonColor() {
            return this.seasonDetails.buttonColor;
        },
    },
    created() {}
}
</script>
